import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import ErrorModal from '@acorn-portal/shared/src/pages/cancellation/components/modal/error-modal';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';
import { Box, Button } from '@mui/material';
import { CancellationEligibilityManager } from '../../../hooks/journey-access/cancellation/use-cancellation-eligibility-manager';
import { CancellationAccessConfig } from '../../../types';
import CancellationAccessNotification from './access-notification';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

interface ICancellationJourneyAccess {
	accessConfig: CancellationAccessConfig;
	eligibilityManager: CancellationEligibilityManager;
}

/**
 * - Separation of concerns for the Cancel Policy button and the handling of eligibility checks via notifications and error states
 * - If eligibility checks pass, `accessConfig.fullAccess` is true and the user will be navigated to the cancellations journey
 * - If they fail, the user is informed via the access notification component.
 */
const CancellationJourneyAccess = ({ accessConfig, eligibilityManager }: ICancellationJourneyAccess) => {
	const product = useAppSelector((state) => state.fullPolicy.product);
	const checkedEligibility = useAppSelector((state) => state.cancellation.checkedEligibility);
	const contactNumber = getPhoneNumber(product, 'CONTACT');
	const { track } = useTrack();

	const onCancelPolicyClick = () => {
		track('cancellation_cancel_policy_button');
		eligibilityManager.handleClick();
	};

	return accessConfig.baseAccess ? (
		<>
			<Button
				color='error'
				variant='contained'
				onClick={onCancelPolicyClick}
				disabled={checkedEligibility && !accessConfig.fullAccess}
				fullWidth
				size='large'
			>
				Cancel Policy
			</Button>
			<LoadingModal open={eligibilityManager.isLoading} message='Checking eligibility' />
			<ErrorModal
				open={eligibilityManager.hasError}
				onClose={eligibilityManager.resetError}
				contactNumber={contactNumber}
			/>
			{accessConfig.accessMessageConfig.show && (
				<Box sx={{ mt: '1rem' }}>
					<CancellationAccessNotification reasonBlocked={accessConfig.accessMessageConfig.reasonBlocked} />
				</Box>
			)}
		</>
	) : null;
};

export default CancellationJourneyAccess;
